import React, { useState, useEffect } from "react";
import {
  FaUsers,
  FaTrophy,
  FaDumbbell,
  FaFutbol,
  FaBasketballBall,
  FaVolleyballBall,
  FaFootballBall,
  FaUtensils,
  FaClock,
  FaMapMarkerAlt,
  FaCalendarAlt,
} from "react-icons/fa";
import {
  GiSoccerField,
  GiCricketBat,
  GiBoxingGlove,
  GiCorn,
  GiWeightLiftingUp,
} from "react-icons/gi";
import { MdSportsGymnastics } from "react-icons/md";
import { BiDrink } from "react-icons/bi";

const GlobalStyles = () => (
  <style>
    {`
      @keyframes fadeIn { 
        from { opacity: 0; transform: translateY(20px); } 
        to { opacity: 1; transform: translateY(0); } 
      }
      
      @keyframes slideInLeft {
        from { opacity: 0; transform: translateX(-30px); }
        to { opacity: 1; transform: translateX(0); }
      }
      
      @keyframes slideInRight {
        from { opacity: 0; transform: translateX(30px); }
        to { opacity: 1; transform: translateX(0); }
      }
      
      @keyframes scaleIn {
        from { opacity: 0; transform: scale(0.9); }
        to { opacity: 1; transform: scale(1); }
      }
      
      @keyframes float {
        0% { transform: translateY(0px); }
        50% { transform: translateY(-5px); }
        100% { transform: translateY(0px); }
      }
      
      .animate-fadeIn { animation: fadeIn 0.6s ease-out forwards; }
      .animate-slideInLeft { animation: slideInLeft 0.7s ease-out forwards; }
      .animate-slideInRight { animation: slideInRight 0.7s ease-out forwards; }
      .animate-scaleIn { animation: scaleIn 0.5s ease-out forwards; }
      .animate-float { animation: float 3s ease-in-out infinite; }
      
      .gradient-text {
        background: linear-gradient(135deg, #F53A5F, #f97316);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }
      
      .gradient-bg {
        background: linear-gradient(135deg, #F53A5F, #f97316);
      }
      
      .custom-scrollbar::-webkit-scrollbar { width: 6px; }
      .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; }
      .custom-scrollbar::-webkit-scrollbar-thumb { 
        background: linear-gradient(135deg, #F53A5F, #f97316);
        border-radius: 10px; 
      }
      
      .hover-lift {
        transition: transform 0.3s ease;
      }
      .hover-lift:hover {
        transform: translateY(-4px);
      }
      
      .skeleton-pulse {
        animation: pulse 1.5s ease-in-out infinite;
      }
      
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
      
      .text-shadow {
        text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
      }
    `}
  </style>
);

const SkeletonCard = () => (
  <div className="bg-gray-200 rounded-xl h-24 skeleton-pulse"></div>
);

const About = () => {
  const [loading, setLoading] = useState(true);
  const [activeSport, setActiveSport] = useState(null);

  useEffect(() => {
    // Simulating API load with realistic timing
    const timer = setTimeout(() => setLoading(false), 100);
    return () => clearTimeout(timer);
  }, []);

  const sports = [
    {
      id: "cricket",
      icon: GiCricketBat,
      name: "Cricket",
      desc: "Six dedicated practice cages on the Green turf, plus box cricket setups on the Red and Black fields.",
      features: [
        "3 Box Cricket Fields",
        "6 Practice Cages",
        "Tournament Ready",
      ],
      color: "from-green-500 to-emerald-600",
    },
    {
      id: "futsal",
      icon: FaFutbol,
      name: "Futsal (Soccer)",
      desc: "Blue, Green, Red, and Black turf fields ideal for futsal training, pickup games, and competitive leagues.",
      features: [
        "4 Concurrent Matches",
        "Pro Turf Surface",
        "League Play",
      ],
      color: "from-blue-500 to-cyan-600",
    },
    {
      id: "basketball",
      icon: FaBasketballBall,
      name: "Basketball",
      desc: "The Blue hard court transforms seamlessly into a full-size basketball court for coaching programs and tournaments.",
      features: ["Full Court", "Coaching Programs", "Tournament Play"],
      color: "from-orange-500 to-red-600",
    },
    {
      id: "volleyball",
      icon: FaVolleyballBall,
      name: "Volleyball",
      desc: "The Blue court converts into two professional volleyball courts for coaching, scrimmages, and competitive play.",
      features: ["2 Pro Courts", "Coaching Programs", "Scrimmages"],
      color: "from-yellow-500 to-orange-600",
    },
    {
      id: "lacrosse",
      icon: GiBoxingGlove,
      name: "Lacrosse",
      desc: "Red and Black turf fields provide excellent space for lacrosse training sessions, clinics, and competitive play.",
      features: ["Training Sessions", "Clinics", "Competitive Play"],
      color: "from-purple-500 to-pink-600",
    },
    {
      id: "football",
      icon: FaFootballBall,
      name: "American Football",
      desc: "Red and Black fields configured for football practices, scrimmages, and draft-style leagues.",
      features: ["Draft Leagues", "Practices", "Scrimmages"],
      color: "from-red-500 to-red-700",
    },
    {
      id: "gymnastics",
      icon: MdSportsGymnastics,
      name: "Gymnastics",
      desc: "A dedicated gymnastics zone on the Green turf, equipped with professional mats for safe, structured training.",
      features: ["Pro Mats", "Structured Training", "Safe Zone"],
      color: "from-pink-500 to-rose-600",
    },
    {
      id: "cornhole",
      icon: GiCorn,
      name: "Cornhole & Events",
      desc: "Flexible field layouts support cornhole tournaments and multi-field events for leagues and corporate events.",
      features: ["Tournaments", "Corporate Events", "Community Events"],
      color: "from-amber-500 to-yellow-600",
    },
  ];

  if (loading) {
    return (
      <div className="bg-white min-h-screen font-poppins">
        <GlobalStyles />
        <div className="relative h-[50vh] bg-gray-300 skeleton-pulse"></div>
        <div className="flex flex-col lg:flex-row min-h-screen">
          <div className="lg:w-7/12 p-8 lg:p-12">
            <div className="h-10 w-48 bg-gray-200 rounded mb-4 skeleton-pulse"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[...Array(8)].map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          </div>
          <div className="lg:w-5/12">
            <div className="h-96 bg-gray-300 skeleton-pulse"></div>
            <div className="h-1 bg-red-500"></div>
            <div className="h-96 bg-gray-200 skeleton-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen font-poppins">
      <GlobalStyles />

      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-red-100 rounded-full blur-3xl animate-float opacity-30"></div>
        <div
          className="absolute bottom-20 right-10 w-96 h-96 bg-orange-100 rounded-full blur-3xl animate-float opacity-30"
          style={{ animationDelay: "1s" }}
        ></div>
      </div>

      {/* PANEL 1: FULL-WIDTH INTRO - Enhanced with provided content */}
      <section className="relative h-[70vh] min-h-[600px] flex items-center justify-center overflow-hidden group">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1531415074968-036ba1b575da?auto=format&fit=crop&w=1950&q=80"
            alt="Hero"
            className="w-full h-full object-cover brightness-50 scale-105 group-hover:scale-100 transition-transform duration-7000"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-white/10"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,#F53A5F30,transparent_60%)]"></div>
        </div>

        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-3 mb-6 animate-scaleIn">
            <div className="w-8 h-[2px] bg-gradient-to-r from-red-500 to-orange-500"></div>
            <span className="text-gray-400 font-subheading tracking-[0.3em] text-sm uppercase">
              About
            </span>
            <div className="w-8 h-[2px] bg-gradient-to-r from-orange-500 to-red-500"></div>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 text-shadow animate-fadeIn text-[#f9f0f0] font-heading tracking-wide">
            Where Passion{" "}
            <span className="gradient-text inline-block hover:scale-105 transition-transform duration-300">
              Meets Performance
            </span>
          </h1>

          <p
            className="text-gray-200 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-8 animate-fadeIn font-poppins tracking-wider"
            style={{ animationDelay: "0.2s" }}
          >
            Strikes & Shots is a climate-controlled, multi-sport indoor destination built for athletes, families, and teams who want more than just a place to play — they want a premium, year-round experience.
          </p>
          

          <p
            className="text-red-300 text-base md:text-lg max-w-3xl mx-auto leading-relaxed mb-8 animate-fadeIn font-poppins italic"
            style={{ animationDelay: "0.5s" }}
          >
            Our focus is simple: exceptional facilities, an unmatched playing experience, and a vibrant community that makes you want to come back again and again.
          </p>

          <div
            className="flex flex-wrap gap-4 justify-center animate-fadeIn font-poppins tracking-wide"
            style={{ animationDelay: "0.6s" }}
          >
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <FaClock className="text-red-400 text-sm" />
              <span className="text-white text-sm">Open 24/7</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <FaMapMarkerAlt className="text-red-400 text-sm" />
              <span className="text-white text-sm">Climate-Controlled Indoor Facility</span>
            </div>
          </div>
        </div>
      </section>

      {/* SPLIT PANEL DESIGN */}
      <div className="flex flex-col lg:flex-row min-h-screen relative">
        {/* PANEL 2: SPORTS (BOTTOM LEFT) - 2x4 Grid with detailed content */}
        <section className="lg:w-7/12 p-8 lg:p-12 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 left-0 w-64 h-64 bg-red-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-orange-500 rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10">
            <div className="mb-10 animate-slideInLeft">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-12 h-1 gradient-bg rounded-full"></span>
                <span className="text-red-600 font-subheading tracking-wider text-sm uppercase">
                  Multi-Sport Experience
                </span>
              </div>
              <h2 className="text-4xl font-heading font-bold text-gray-900 border-l-4 border-red-500 pl-4 mb-3">
                Turf & Court
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl font-poppins">
                Our best-in-class indoor turf and court layout is designed for true multi-sport flexibility — allowing multiple games, practices, and leagues to run simultaneously in a premium, climate-controlled environment.
              </p>
              <p className="text-gray-500 text-base max-w-2xl mt-2 font-poppins font-medium">
                Built for versatility. Designed for performance.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {sports.map((sport, index) => (
                <div
                  key={sport.id}
                  className="group relative bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 animate-scaleIn"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onMouseEnter={() => setActiveSport(sport.id)}
                  onMouseLeave={() => setActiveSport(null)}
                >
                  {/* Hover Gradient Overlay */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${sport.color} opacity-0 group-hover:opacity-5 rounded-xl transition-opacity duration-500`}
                  ></div>

                  <div className="flex items-start gap-4">
                    <div
                      className={`p-4 rounded-xl bg-gradient-to-br ${sport.color} group-hover:scale-110 transition-transform duration-300 shadow-lg`}
                    >
                      <sport.icon className="text-2xl text-white" />
                    </div>

                    <div className="flex-1">
                      <h3 className="font-heading font-bold text-gray-800 text-lg mb-1 flex items-center gap-2">
                        {sport.name}
                        {sport.id === "cricket" && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">🏏</span>}
                        {sport.id === "futsal" && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">⚽</span>}
                        {sport.id === "basketball" && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">🏀</span>}
                        {sport.id === "volleyball" && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">🏐</span>}
                        {sport.id === "lacrosse" && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">🥍</span>}
                        {sport.id === "football" && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">🏈</span>}
                        {sport.id === "gymnastics" && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">🤸</span>}
                        {sport.id === "cornhole" && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">🎯</span>}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2 font-poppins">{sport.desc}</p>

                      {/* Features Tags */}
                      <div className="flex flex-wrap gap-2 mt-2">
                        {sport.features.map((feature, i) => (
                          <span
                            key={i}
                            className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-poppins"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Active Indicator */}
                  {activeSport === sport.id && (
                    <div className="absolute top-3 right-3">
                      <span className="flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Multi-Sport Message */}
            <div className="mt-10 p-6 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border border-red-100 animate-fadeIn">
              <h3 className="text-2xl font-heading font-bold text-gray-800 mb-2 text-center">
                One Facility. Multiple Sports. Endless Possibilities.
              </h3>
              <p className="text-gray-600 text-center font-poppins">
                Whether you're training, competing, or hosting an event, Strikes & Shots delivers the space, quality, and energy to elevate every game.
              </p>
            </div>
          </div>
        </section>

        {/* PANEL 3: GYM + BAR (RIGHT COLUMN STACK) */}
        <section className="lg:w-5/12 flex flex-col border-t-4 lg:border-t-0 lg:border-l-4 border-red-500 relative">
          {/* TOP HALF: GYM - Enhanced with provided content */}
          <div className="flex-1 relative group overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800 text-white p-8 lg:p-12 min-h-[400px]">
            {/* Background Image with Parallax */}
            <div className="absolute inset-0">
              <img
                src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=800&q=80"
                className="w-full h-full object-cover opacity-30 group-hover:scale-110 transition-transform duration-1000"
                alt="Gym"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent"></div>
            </div>

            {/* Content */}
            <div className="relative z-10 flex flex-col h-full justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4 animate-slideInRight">
                  <div className="p-3 bg-red-500/20 rounded-xl backdrop-blur-sm">
                    <FaDumbbell className="text-3xl text-red-500" />
                  </div>
                  <div>
                    <span className="text-red-400 font-subheading text-sm font-semibold tracking-wider uppercase">
                      High-Performance
                    </span>
                    <h2 className="text-3xl font-heading font-bold">Gymnasium</h2>
                  </div>
                </div>

                <p
                  className="text-gray-300 leading-relaxed mb-4 animate-fadeIn font-poppins"
                  style={{ animationDelay: "0.2s" }}
                >
                  Our fully equipped gymnasium is designed to support athletes and members who are serious about fitness, strength, and performance. With modern weight and conditioning equipment, the gym covers strength training, endurance building, mobility work, and injury prevention.
                </p>
                
                <p
                  className="text-gray-300 leading-relaxed mb-6 animate-fadeIn font-poppins text-sm"
                  style={{ animationDelay: "0.3s" }}
                >
                  From functional training zones and free weights to sport-specific conditioning machines — every element is curated to help you train smarter and perform better.
                </p>

                {/* Features Grid */}
                <div
                  className="grid grid-cols-2 gap-4 mb-6 animate-fadeIn font-poppins"
                  style={{ animationDelay: "0.4s" }}
                >
                  {[
                    "Strength Training",
                    "Endurance Building",
                    "Mobility Work",
                    "Injury Prevention",
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-1 h-1 bg-red-500 rounded-full"></div>
                      <span className="text-sm text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <span className="inline-block text-red-400 font-subheading font-semibold text-sm tracking-widest uppercase animate-pulse">
                  ⚡ Train. Recover. Get Stronger.
                </span>

                {/* Coach Badge */}
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white font-heading font-bold">
                    PT
                  </div>
                  <div>
                    <p className="text-sm font-heading font-semibold">
                      Elite Coaches Available
                    </p>
                    <p className="text-xs text-gray-400 font-poppins">
                      Personal training & programs
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* DIVIDER LINE - Enhanced */}
          <div className="relative h-2">
            <div className="absolute inset-0 bg-gradient-to-r from-red-500 via-orange-500 to-red-500"></div>
            <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-12 h-4 bg-white rounded-full border-2 border-red-500 flex items-center justify-center">
              <BiDrink className="text-red-500 text-xs" />
            </div>
          </div>

          {/* BOTTOM HALF: BAR & RESTAURANT - Enhanced with provided content */}
          <div className="flex-1 relative group overflow-hidden bg-gradient-to-br from-amber-50 to-orange-50 p-8 lg:p-12 min-h-[400px]">
            {/* Background Image */}
            <div className="absolute inset-0">
              <img
                src="https://images.unsplash.com/photo-1514933651103-005eec06c04b?auto=format&fit=crop&w=800&q=80"
                className="w-full h-full object-cover opacity-20 group-hover:scale-110 transition-transform duration-1000"
                alt="Bar"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/50"></div>
            </div>

            {/* Content */}
            <div className="relative z-10 flex flex-col h-full justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4 animate-slideInRight">
                  <div className="p-3 bg-red-100 rounded-xl">
                    <FaUtensils className="text-3xl text-red-600" />
                  </div>
                  <div>
                    <span className="text-red-600 font-subheading text-sm font-semibold tracking-wider uppercase">
                      Refuel & Relax
                    </span>
                    <h2 className="text-3xl font-heading font-bold text-gray-900">
                      Bar, Bites & Good Times
                    </h2>
                  </div>
                </div>

                <p
                  className="text-gray-700 leading-relaxed mb-4 animate-fadeIn font-poppins"
                  style={{ animationDelay: "0.2s" }}
                >
                  The action doesn't stop when the game ends. Our bar and restaurant offers the perfect place to relax, refuel, and connect — with a menu of delicious finger foods and crowd favorites paired with a full-service bar.
                </p>
                
                <p
                  className="text-gray-700 leading-relaxed mb-6 animate-fadeIn font-poppins text-sm"
                  style={{ animationDelay: "0.3s" }}
                >
                  Bring your teammates, friends, and family to hang out, catch the games, celebrate wins, and make every visit to Strikes & Shots a complete experience — on and off the field.
                </p>

                {/* Menu Highlights */}
                <div
                  className="flex flex-wrap gap-2 mb-6 animate-fadeIn font-poppins"
                  style={{ animationDelay: "0.4s" }}
                >
                  {[
                    "Craft Beer",
                    "Full-Service Bar",
                    "Finger Foods",
                    "Crowd Favorites",
                  ].map((item, i) => (
                    <span
                      key={i}
                      className="bg-white/80 backdrop-blur-sm text-sm px-3 py-1 rounded-full border border-red-200"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                {/* Quote Card */}
                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-5 border-l-4 border-red-500 shadow-lg hover:shadow-xl transition-shadow">
                  <p className="text-gray-900 font-heading font-medium italic flex items-start gap-2 text-lg">
                    <span className="text-2xl text-red-500">"</span>
                    Work Up a Sweat. Then Treat Yourself.
                    <span className="text-2xl text-red-500 self-end">"</span>
                  </p>

                  {/* Hours */}
                  <div className="flex items-center gap-4 mt-3 text-sm text-gray-600 font-poppins">
                    <div className="flex items-center gap-1">
                      <FaClock className="text-red-500" />
                      <span>Mon-Sun: 11am - 2am</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <FaCalendarAlt className="text-red-500" />
                      <span>Happy Hour: 4-7pm</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* COMPACT FOOTER STATS - Enhanced */}
      <footer className="bg-gradient-to-r from-gray-50 to-white py-10 border-t border-gray-200 px-8 mb-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-between items-center gap-6">
            <div className="flex items-center gap-3 group hover-lift">
              <div className="p-3 bg-red-50 rounded-lg group-hover:bg-red-500 transition-colors">
                <FaUsers className="text-xl text-red-500 group-hover:text-white" />
              </div>
              <div>
                <span className="font-heading font-bold text-2xl text-gray-900">10k+</span>
                <span className="text-gray-600 ml-2 font-poppins">Athletes</span>
              </div>
            </div>

            <div className="w-px h-10 bg-gray-300 hidden md:block"></div>

            <div className="flex items-center gap-3 group hover-lift">
              <div className="p-3 bg-red-50 rounded-lg group-hover:bg-red-500 transition-colors">
                <FaTrophy className="text-xl text-red-500 group-hover:text-white" />
              </div>
              <div>
                <span className="font-heading font-bold text-2xl text-gray-900">200+</span>
                <span className="text-gray-600 ml-2 font-poppins">Events</span>
              </div>
            </div>

            <div className="w-px h-10 bg-gray-300 hidden md:block"></div>

            <div className="flex items-center gap-3 group hover-lift">
              <div className="p-3 bg-red-50 rounded-lg group-hover:bg-red-500 transition-colors">
                <GiSoccerField className="text-xl text-red-500 group-hover:text-white" />
              </div>
              <div>
                <span className="font-heading font-bold text-2xl text-gray-900">8</span>
                <span className="text-gray-600 ml-2 font-poppins">Pro Fields</span>
              </div>
            </div>

            <div className="w-px h-10 bg-gray-300 hidden md:block"></div>

            <div className="flex items-center gap-3 group hover-lift">
              <div className="p-3 bg-red-50 rounded-lg group-hover:bg-red-500 transition-colors">
                <GiWeightLiftingUp className="text-xl text-red-500 group-hover:text-white" />
              </div>
              <div>
                <span className="font-heading font-bold text-2xl text-gray-900">24/7</span>
                <span className="text-gray-600 ml-2 font-poppins">Access</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;

// import React, { useState, useEffect } from "react";
// import {
//   FaUsers,
//   FaTrophy,
//   FaDumbbell,
//   FaFutbol,
//   FaBasketballBall,
//   FaVolleyballBall,
//   FaFootballBall,
//   FaUtensils,
// } from "react-icons/fa";
// import {
//   GiSoccerField,
//   GiCricketBat,
//   GiBoxingGlove,
//   GiCorn,
// } from "react-icons/gi";
// import { MdSportsGymnastics } from "react-icons/md";
// import axios from "axios";

// const GlobalStyles = () => (
//   <style>
//     {`
//       @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
//       .animate-fadeIn { animation: fadeIn 0.5s ease-out forwards; }
//       .gradient-text {
//         background: linear-gradient(135deg, #F53A5F, #f97316);
//         -webkit-background-clip: text;
//         -webkit-text-fill-color: transparent;
//       }
//       .custom-scrollbar::-webkit-scrollbar { width: 6px; }
//       .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; }
//       .custom-scrollbar::-webkit-scrollbar-thumb { background: #F53A5F; border-radius: 10px; }
//     `}
//   </style>
// );

// const About = () => {
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     // Simulating API load or logic
//     const timer = setTimeout(() => setLoading(false), 800);
//     return () => clearTimeout(timer);
//   }, []);

//   const sports = [
//     { id: "cricket", icon: GiCricketBat, name: "Cricket", desc: "6 dedicated practice cages & box setups." },
//     { id: "futsal", icon: FaFutbol, name: "Futsal", desc: "Premium turf fields for competitive leagues." },
//     { id: "basketball", icon: FaBasketballBall, name: "Basketball", desc: "Full-size hard court for pro play." },
//     { id: "volleyball", icon: FaVolleyballBall, name: "Volleyball", desc: "Dual professional court conversions." },
//     { id: "lacrosse", icon: GiBoxingGlove, name: "Lacrosse", desc: "Training sessions and clinic spaces." },
//     { id: "football", icon: FaFootballBall, name: "Football", desc: "Configured for draft-style leagues." },
//     { id: "gymnastics", icon: MdSportsGymnastics, name: "Gymnastics", desc: "Professional mats and safety zones." },
//     { id: "cornhole", icon: GiCorn, name: "Cornhole", desc: "Multi-field layout for tournaments." },
//   ];

//   if (loading) return <div className="h-screen flex items-center justify-center">Loading...</div>;

//   return (
//     <div className="bg-white min-h-screen font-sans">
//       <GlobalStyles />

//       {/* PANEL 1: FULL-WIDTH INTRO */}
//       <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
//         <div className="absolute inset-0 z-0">
//           <img
//             src="https://images.unsplash.com/photo-1531415074968-036ba1b575da?auto=format&fit=crop&w=1950&q=80"
//             alt="Hero"
//             className="w-full h-full object-cover brightness-50"
//           />
//           <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-white"></div>
//         </div>

//         <div className="relative z-10 text-center px-4 animate-fadeIn">
//           <h1 className="text-5xl md:text-7xl font-bold mb-4">
//             Where Passion <span className="gradient-text">Meets Performance</span>
//           </h1>
//           <p className="text-gray-700 text-lg md:text-xl max-w-2xl mx-auto">
//             A premium, climate-controlled multi-sport destination built for athletes who demand the best.
//           </p>
//         </div>
//       </section>

//       {/* SPLIT PANEL DESIGN */}
//       <div className="flex flex-col lg:flex-row min-h-screen">

//         {/* PANEL 2: SPORTS (BOTTOM LEFT) - 2x4 Grid */}
//         <section className="lg:w-7/12 p-8 lg:p-12 bg-gray-50">
//           <div className="mb-10">
//             <h2 className="text-3xl font-bold text-gray-900 border-l-4 border-red-500 pl-4">Sports Arena</h2>
//             <p className="text-gray-600 mt-2">Elite turf and court setups for every discipline.</p>
//           </div>

//           <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
//             {sports.map((sport) => (
//               <div
//                 key={sport.id}
//                 className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 group"
//               >
//                 <div className="flex items-center gap-4">
//                   <div className="p-3 bg-red-50 rounded-lg group-hover:bg-red-500 transition-colors">
//                     <sport.icon className="text-2xl text-red-500 group-hover:text-white" />
//                   </div>
//                   <div>
//                     <h3 className="font-bold text-gray-800 uppercase tracking-tight">{sport.name}</h3>
//                     <p className="text-sm text-gray-500 line-clamp-2">{sport.desc}</p>
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </section>

//         {/* PANEL 3: GYM + BAR (RIGHT COLUMN STACK) */}
//         <section className="lg:w-5/12 flex flex-col border-t-4 lg:border-t-0 lg:border-l-4 border-red-500">

//           {/* TOP HALF: GYM */}
//           <div className="flex-1 relative group overflow-hidden bg-gray-900 text-white p-8 lg:p-12">
//             <img
//               src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=800&q=80"
//               className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:scale-105 transition-transform duration-700"
//               alt="Gym"
//             />
//             <div className="relative z-10 flex flex-col h-full justify-center">
//               <div className="flex items-center gap-3 mb-4">
//                 <FaDumbbell className="text-3xl text-red-500" />
//                 <h2 className="text-3xl font-bold">High-Performance Gym</h2>
//               </div>
//               <p className="text-gray-300 leading-relaxed mb-4">
//                 Fully equipped with modern strength and conditioning gear.
//                 Built for athletes focused on endurance, mobility, and injury prevention.
//               </p>
//               <span className="text-red-400 font-semibold text-sm tracking-widest uppercase">Train Smarter. Perform Better.</span>
//             </div>
//           </div>

//           {/* DIVIDER LINE (Visible on stack) */}
//           <div className="h-1 bg-red-500 w-full"></div>

//           {/* BOTTOM HALF: BAR & RESTAURANT */}
//           <div className="flex-1 relative group overflow-hidden bg-gray-100 p-8 lg:p-12">
//             <img
//               src="https://images.unsplash.com/photo-1514933651103-005eec06c04b?auto=format&fit=crop&w=800&q=80"
//               className="absolute inset-0 w-full h-full object-cover opacity-10 group-hover:scale-105 transition-transform duration-700"
//               alt="Bar"
//             />
//             <div className="relative z-10 flex flex-col h-full justify-center">
//               <div className="flex items-center gap-3 mb-4 text-gray-900">
//                 <FaUtensils className="text-3xl text-red-600" />
//                 <h2 className="text-3xl font-bold">Bar & Bites</h2>
//               </div>
//               <p className="text-gray-700 leading-relaxed mb-4">
//                 The action doesn’t stop at the whistle. Refuel with delicious finger foods
//                 and refreshing beverages in our social lounge.
//               </p>
//               <div className="p-4 bg-white/80 rounded-lg border-l-4 border-red-500">
//                 <p className="text-gray-900 font-medium italic italic">"Work Up a Sweat. Then Treat Yourself."</p>
//               </div>
//             </div>
//           </div>

//         </section>
//       </div>

//       {/* COMPACT FOOTER STATS */}
//       <footer className="bg-white py-8 border-t border-gray-100 px-8">
//         <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-8">
//           <div className="flex items-center gap-2"><FaUsers className="text-red-500"/> <span className="font-bold">10k+ Athletes</span></div>
//           <div className="flex items-center gap-2"><FaTrophy className="text-red-500"/> <span className="font-bold">200+ Events</span></div>
//           <div className="flex items-center gap-2"><GiSoccerField className="text-red-500"/> <span className="font-bold">8 Pro Fields</span></div>
//           <div className="flex items-center gap-2"><FaDumbbell className="text-red-500"/> <span className="font-bold">Elite Coaching</span></div>
//         </div>
//       </footer>
//     </div>
//   );
// };

// export default About;

// import React, { useState, useEffect } from "react";
// import {
//   FaUsers,
//   FaTrophy,
//   FaDumbbell,
//   FaFutbol,
//   FaBasketballBall,
//   FaVolleyballBall,
//   FaFootballBall,
// } from "react-icons/fa";
// import {
//   GiSoccerField,
//   GiCricketBat,
//   GiBoxingGlove,
//   GiCorn,
// } from "react-icons/gi";
// import { MdSportsGymnastics } from "react-icons/md";
// import axios from "axios";

// // Add this style tag for custom animations and scrollbar
// const GlobalStyles = () => (
//   <style>
//     {`
//       @keyframes fadeIn {
//         from {
//           opacity: 0;
//           transform: translateY(20px);
//         }
//         to {
//           opacity: 1;
//           transform: translateY(0);
//         }
//       }

//       @keyframes float {
//         0% {
//           transform: translateY(0px);
//         }
//         50% {
//           transform: translateY(-10px);
//         }
//         100% {
//           transform: translateY(0px);
//         }
//       }

//       @keyframes pulse {
//         0%, 100% {
//           opacity: 1;
//         }
//         50% {
//           opacity: 0.5;
//         }
//       }

//       @keyframes slideInLeft {
//         from {
//           opacity: 0;
//           transform: translateX(-30px);
//         }
//         to {
//           opacity: 1;
//           transform: translateX(0);
//         }
//       }

//       @keyframes slideInRight {
//         from {
//           opacity: 0;
//           transform: translateX(30px);
//         }
//         to {
//           opacity: 1;
//           transform: translateX(0);
//         }
//       }

//       @keyframes scaleIn {
//         from {
//           opacity: 0;
//           transform: scale(0.9);
//         }
//         to {
//           opacity: 1;
//           transform: scale(1);
//         }
//       }

//       .animate-fadeIn {
//         animation: fadeIn 0.6s ease-out forwards;
//       }

//       .animate-float {
//         animation: float 3s ease-in-out infinite;
//       }

//       .animate-pulse-slow {
//         animation: pulse 2s ease-in-out infinite;
//       }

//       .animate-slideInLeft {
//         animation: slideInLeft 0.8s ease-out forwards;
//       }

//       .animate-slideInRight {
//         animation: slideInRight 0.8s ease-out forwards;
//       }

//       .animate-scaleIn {
//         animation: scaleIn 0.5s ease-out forwards;
//       }

//       /* Custom scrollbar for white theme */
//       ::-webkit-scrollbar {
//         width: 12px;
//       }

//       ::-webkit-scrollbar-track {
//         background: #f1f1f1;
//       }

//       ::-webkit-scrollbar-thumb {
//         background: linear-gradient(180deg, #F53A5F 0%, #f97316 100%);
//         border-radius: 6px;
//         border: 2px solid #f1f1f1;
//       }

//       ::-webkit-scrollbar-thumb:hover {
//         background: linear-gradient(180deg, #dc2626 0%, #ea580c 100%);
//       }

//       /* Smooth scrolling */
//       html {
//         scroll-behavior: smooth;
//       }

//       /* Text selection */
//       ::selection {
//         background: #F53A5F;
//         color: white;
//       }

//       /* Loading skeleton animation */
//       .skeleton-pulse {
//         animation: pulse 1.5s ease-in-out infinite;
//       }

//       /* Gradient text */
//       .gradient-text {
//         background: linear-gradient(135deg, #F53A5F, #f97316);
//         -webkit-background-clip: text;
//         -webkit-text-fill-color: transparent;
//         background-clip: text;
//       }

//       /* Hover effects */
//       .hover-glow {
//         transition: all 0.3s ease;
//       }
//       .hover-glow:hover {
//         box-shadow: 0 0 20px rgba(245, 58, 95, 0.3);
//       }

//       /* Image overlay */
//       .image-overlay {
//         position: relative;
//         overflow: hidden;
//       }
//       .image-overlay::after {
//         content: '';
//         position: absolute;
//         top: 0;
//         left: 0;
//         right: 0;
//         bottom: 0;
//         background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.8) 100%);
//         pointer-events: none;
//       }
//     `}
//   </style>
// );

// // Skeleton Components
// const BannerSkeleton = () => (
//   <div className="relative w-full h-64 sm:h-80 md:h-96 lg:h-[500px]">
//     <GlobalStyles />
//     <div className="absolute inset-0 bg-gray-200 skeleton-pulse"></div>
//     <div className="relative z-10 flex flex-col justify-center items-center h-full px-4">
//       <div className="h-12 w-72 bg-gray-300 rounded-lg mb-4 skeleton-pulse"></div>
//       <div className="h-6 w-96 bg-gray-300 rounded-md skeleton-pulse"></div>
//     </div>
//   </div>
// );

// const About = () => {
//   const [aboutData, setAboutData] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [activeSport, setActiveSport] = useState("futsal");

//   useEffect(() => {
//     const fetchAboutData = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_API_URL}/about/get-about`
//         );
//         if (response.data?.success && response.data?.data) {
//           setAboutData(response.data.data);
//         }
//       } catch (error) {
//         console.error("Error fetching about data:", error);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchAboutData();
//   }, []);

//   if (loading) {
//     return (
//       <div>
//         <GlobalStyles />
//         <BannerSkeleton />
//         <div className="py-12 px-4 md:px-8 lg:px-12 bg-white">
//           <div className="max-w-7xl mx-auto">
//             <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
//               <div className="space-y-6">
//                 <div className="h-10 w-48 bg-gray-200 rounded-lg skeleton-pulse"></div>
//                 <div className="h-4 w-full bg-gray-200 rounded skeleton-pulse"></div>
//                 <div className="h-4 w-full bg-gray-200 rounded skeleton-pulse"></div>
//                 <div className="h-4 w-3/4 bg-gray-200 rounded skeleton-pulse"></div>
//               </div>
//               <div className="grid grid-cols-2 gap-6">
//                 {[1, 2, 3, 4].map((i) => (
//                   <div
//                     key={i}
//                     className="h-40 bg-gray-100 rounded-xl skeleton-pulse"
//                   ></div>
//                 ))}
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   const sports = [
//     {
//       id: "futsal",
//       icon: FaFutbol,
//       name: "Futsal",
//       description:
//         "Our Blue, Green, Red, and Black turf fields are ideal for futsal training, pickup games, and competitive leagues. With flexible field markings and layout, up to four matches can run in parallel.",
//       color: "from-blue-500 to-cyan-500",
//     },
//     {
//       id: "cricket",
//       icon: GiCricketBat,
//       name: "Cricket",
//       description:
//         "Six dedicated practice cages on the Green turf, along with additional box cricket setups on the Red and Black fields. Host three concurrent box cricket matches.",
//       color: "from-green-500 to-emerald-500",
//     },
//     {
//       id: "basketball",
//       icon: FaBasketballBall,
//       name: "Basketball",
//       description:
//         "The Blue hard court transforms seamlessly into a full-size basketball court, ideal for coaching programs, scrimmages, and tournament play.",
//       color: "from-orange-500 to-red-500",
//     },
//     {
//       id: "volleyball",
//       icon: FaVolleyballBall,
//       name: "Volleyball",
//       description:
//         "The Blue court converts into two professional volleyball courts, perfect for coaching programs, scrimmages, and tournament play.",
//       color: "from-yellow-500 to-orange-500",
//     },
//     {
//       id: "lacrosse",
//       icon: GiBoxingGlove,
//       name: "Lacrosse",
//       description:
//         "Red and Black turf fields provide excellent space for lacrosse training sessions, clinics, and competitive play.",
//       color: "from-purple-500 to-pink-500",
//     },
//     {
//       id: "football",
//       icon: FaFootballBall,
//       name: "American Football",
//       description:
//         "The Red and Black fields are configured for American football practices, scrimmages, and draft-style leagues.",
//       color: "from-red-500 to-red-700",
//     },
//     {
//       id: "gymnastics",
//       icon: MdSportsGymnastics,
//       name: "Gymnastics",
//       description:
//         "A dedicated gymnastics zone on the Green turf equipped with professional mats for safe gymnastics training and development.",
//       color: "from-pink-500 to-rose-500",
//     },
//     {
//       id: "cornhole",
//       icon: GiCorn,
//       name: "Cornhole",
//       description:
//         "Flexible field layout allows for cornhole tournaments and multi-field events, perfect for corporate events and community competitions.",
//       color: "from-amber-500 to-yellow-500",
//     },
//   ];

//   return (
//     <div className="bg-white min-h-screen font-heading relative">
//       <GlobalStyles />

//       {/* Animated Background Elements */}
//       <div className="fixed inset-0 overflow-hidden pointer-events-none">
//         <div className="absolute top-20 left-10 w-72 h-72 bg-red-100 rounded-full blur-3xl animate-float"></div>
//         <div
//           className="absolute bottom-20 right-10 w-96 h-96 bg-orange-100 rounded-full blur-3xl animate-float"
//           style={{ animationDelay: "1s" }}
//         ></div>
//         <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-red-50 to-orange-50 rounded-full blur-3xl"></div>
//       </div>

//       {/* Hero Section */}
//       <section className="relative h-[70vh] min-h-[500px] overflow-hidden">
//         <div className="absolute inset-0">
//           <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/80 to-black/60 z-10"></div>
//           <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,#F53A5F30,transparent_60%)] z-10"></div>
//           <img
//             src="https://images.unsplash.com/photo-1531415074968-036ba1b575da?ixlib=rb-4.0.3&auto=format&fit=crop&w=1950&q=80"
//             alt="Sports Facility"
//             className="w-full h-full object-cover transform scale-105 hover:scale-100 transition-transform duration-7000"
//             style={{ transition: "transform 7s ease" }}
//           />
//         </div>

//         <div className="relative z-20 h-full flex flex-col justify-center items-center text-center px-4">
//           <div className="max-w-4xl mx-auto animate-scaleIn">
//             <div className="inline-block px-6 py-2 rounded-full bg-red-600/20 border border-red-500/30 mb-6 backdrop-blur-sm hover:bg-red-600/30 transition-all duration-300">
//               <span className="text-red-400 font-semibold tracking-wider text-sm uppercase">
//                 Strikes & Shots
//               </span>
//             </div>
//             <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
//               Where Passion{" "}
//               <span className="gradient-text inline-block hover:scale-105 transition-transform duration-300">
//                 Meets Performance
//               </span>
//             </h1>
//             <p
//               className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-8 animate-fadeIn"
//               style={{ animationDelay: "0.3s" }}
//             >
//               A climate-controlled, multi-sport indoor destination built for
//               athletes, families, and teams who want more than just a place to
//               play — they want a premium, year-round experience.
//             </p>
//           </div>
//         </div>
//       </section>

//       {/* Main About Content */}
//       <section className="py-12 px-4 md:px-8 lg:px-12 relative overflow-hidden">
//         <div className="absolute top-0 right-0 w-96 h-96 bg-red-100 rounded-full blur-3xl animate-float"></div>
//         <div
//           className="absolute bottom-0 left-0 w-96 h-96 bg-blue-100 rounded-full blur-3xl animate-float"
//           style={{ animationDelay: "2s" }}
//         ></div>

//         <div className="max-w-7xl mx-auto relative">
//           <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
//             {/* Left Column - Image Collage */}
//             <div className="relative animate-slideInLeft">
//               <div className="grid grid-cols-2 gap-4">
//                 <div className="space-y-4">
//                   <div className="relative overflow-hidden rounded-2xl group image-overlay shadow-xl">
//                     <img
//                       src="https://images.unsplash.com/photo-1577223625816-7546f13df25d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
//                       alt="Indoor Soccer"
//                       className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-700"
//                     />
//                   </div>
//                   <div className="relative overflow-hidden rounded-2xl group image-overlay shadow-xl">
//                     <img
//                       src="https://images.unsplash.com/photo-1546514714-df0ccc50d7bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
//                       alt="Basketball Court"
//                       className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-700"
//                     />
//                   </div>
//                 </div>
//                 <div className="space-y-4 pt-8">
//                   <div className="relative overflow-hidden rounded-2xl group image-overlay shadow-xl">
//                     <img
//                       src="https://images.unsplash.com/photo-1526232761682-d26e03ac148e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
//                       alt="Training Session"
//                       className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-700"
//                     />
//                   </div>
//                   <div className="relative overflow-hidden rounded-2xl group image-overlay shadow-xl">
//                     <img
//                       src="https://images.unsplash.com/photo-1518611012118-696072aa579a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
//                       alt="Facility"
//                       className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-700"
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Right Column - Content */}
//             <div className="space-y-8 animate-slideInRight">
//               <div>
//                 <span className="text-red-600 font-semibold tracking-wider text-sm uppercase relative inline-block after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[2px] after:bg-gradient-to-r after:from-red-500 after:to-transparent">
//                   About Us
//                 </span>
//                 <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
//                   Multi-Sport Turf &{" "}
//                   <span className="gradient-text">Court Experience</span>
//                 </h2>
//                 <p className="text-gray-600 text-lg leading-relaxed mb-6">
//                   At Strikes & Shots, our best-in-class indoor turf and court
//                   layout is designed for true multi-sport flexibility — allowing
//                   multiple games, practices, and leagues to run simultaneously
//                   in a premium, climate-controlled environment.
//                 </p>
//                 <p className="text-gray-600 text-lg leading-relaxed font-semibold">
//                   Built for versatility. Designed for performance.
//                 </p>
//               </div>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* Sports Categories Grid */}
//       <section className="py-12 px-4 md:px-8 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative">
//         <div className="max-w-7xl mx-auto">
//           <div className="text-center mb-16">
//             <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
//               One Facility.{" "}
//               <span className="gradient-text">Multiple Sports.</span> Endless
//               Possibilities.
//             </h2>
//             <p className="text-xl text-gray-600 max-w-3xl mx-auto">
//               Whether you're training, competing, or hosting an event, Strikes &
//               Shots delivers the space, quality, and energy to elevate every
//               game.
//             </p>
//           </div>

//           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
//             {sports.map((sport, idx) => (
//               <div
//                 key={sport.id}
//                 className="group relative animate-scaleIn cursor-pointer h-full"
//                 style={{ animationDelay: `${idx * 0.1}s` }}
//                 onMouseEnter={() => setActiveSport(sport.id)}
//               >
//                 <div
//                   className={`absolute inset-0 bg-gradient-to-br ${sport.color} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-500`}
//                 ></div>
//                 <div className="relative bg-white rounded-2xl p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 h-full flex flex-col">
//                   <div
//                     className={`w-16 h-16 rounded-xl bg-gradient-to-br ${sport.color} flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 flex-shrink-0`}
//                   >
//                     <sport.icon className="text-3xl text-white" />
//                   </div>
//                   <h3 className="text-xl font-bold text-gray-900 mb-2 flex-shrink-0">
//                     {sport.name}
//                   </h3>
//                   <p className="text-gray-600 text-sm leading-relaxed flex-grow">
//                     {sport.description}
//                   </p>
//                   {activeSport === sport.id && (
//                     <div className="absolute top-2 right-2">
//                       <span className="flex h-3 w-3">
//                         <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
//                         <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
//                       </span>
//                     </div>
//                   )}
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* High-Performance Gym Section */}
//       <section className="py-12 px-4 md:px-8 lg:px-12 relative overflow-hidden">
//         <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-700 opacity-5"></div>
//         <div className="max-w-7xl mx-auto relative">
//           <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
//             <div className="space-y-6 animate-slideInLeft">
//               <div className="inline-block px-4 py-2 rounded-full bg-red-100 border border-red-200">
//                 <span className="text-red-600 font-semibold text-sm">
//                   HIGH-PERFORMANCE
//                 </span>
//               </div>
//               <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
//                 Train. Recover.{" "}
//                 <span className="gradient-text">Get Stronger.</span>
//               </h2>
//               <p className="text-gray-600 text-lg leading-relaxed mb-6">
//                 Our fully equipped gymnasium is designed to support athletes and
//                 members who are serious about fitness, strength, and
//                 performance. With a wide range of modern weight and conditioning
//                 equipment, the gym provides everything needed for strength
//                 training, endurance building, mobility work, and injury
//                 prevention.
//               </p>
//               <p className="text-gray-600 text-lg leading-relaxed">
//                 Whether you're an athlete looking to enhance on-field
//                 performance or a member focused on overall fitness, the space is
//                 built to complement your training. From functional training
//                 zones and free weights to machines that support sport-specific
//                 conditioning, every element is curated to help you train smarter
//                 and perform better.
//               </p>
//               <div className="flex items-center gap-4 mt-8">
//                 <div className="flex -space-x-2">
//                   {[1, 2, 3, 4].map((i) => (
//                     <div
//                       key={i}
//                       className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-orange-500 border-2 border-white"
//                     ></div>
//                   ))}
//                 </div>
//                 <p className="text-gray-600">
//                   <span className="font-bold text-gray-900">
//                     Elite Trainers
//                   </span>{" "}
//                   Ready to Assist
//                 </p>
//               </div>
//             </div>
//             <div className="relative animate-slideInRight">
//               <div className="relative rounded-2xl overflow-hidden shadow-2xl">
//                 <img
//                   src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
//                   alt="Gym Facility"
//                   className="w-full h-[500px] object-cover"
//                 />
//                 <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
//                 <div className="absolute bottom-6 left-6 right-6">
//                   <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4">
//                     <p className="text-gray-900 font-semibold">
//                       At Strikes & Shots, the gym completes your journey—making
//                       us a true destination for performance, fitness, and total
//                       athletic development.
//                     </p>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* Bar & Restaurant Section */}
//       <section className="py-12 px-4 md:px-8 lg:px-12 bg-gradient-to-b from-gray-900 to-gray-800 relative overflow-hidden">
//         <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?ixlib=rb-4.0.3&auto=format&fit=crop&w=1950&q=80')] bg-cover bg-center opacity-10"></div>
//         <div className="absolute inset-0 bg-gradient-to-r from-gray-900 to-gray-800/90"></div>

//         <div className="max-w-7xl mx-auto relative z-10">
//           <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
//             <div className="relative order-2 lg:order-1 animate-slideInLeft">
//               <div className="relative rounded-2xl overflow-hidden shadow-2xl">
//                 <img
//                   src="https://images.unsplash.com/photo-1514933651103-005eec06c04b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
//                   alt="Bar and Restaurant"
//                   className="w-full h-[400px] object-cover"
//                 />
//                 <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
//               </div>
//             </div>
//             <div className="space-y-6 order-1 lg:order-2 animate-slideInRight">
//               <div className="inline-block px-4 py-2 rounded-full bg-red-600/20 border border-red-500/30">
//                 <span className="text-red-400 font-semibold text-sm">
//                   REFUEL & RELAX
//                 </span>
//               </div>
//               <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
//                 Bar, Bites & <span className="gradient-text">Good Times</span>
//               </h2>
//               <p className="text-gray-300 text-lg leading-relaxed mb-6">
//                 At Strikes & Shots, the action doesn't stop when the game ends.
//                 Our attached bar and restaurant offers the perfect place to
//                 relax, refuel, and connect after an intense session on the turf
//                 or court.
//               </p>
//               <p className="text-gray-300 text-lg leading-relaxed mb-6">
//                 Enjoy a menu of delicious finger foods and crowd favorites,
//                 paired with a full-service bar serving refreshing beverages for
//                 every taste. Whether you've just finished a tough workout,
//                 wrapped up a competitive match, or are simply here to unwind,
//                 we've created a space where sport meets social.
//               </p>
//               <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
//                 <p className="text-white text-xl font-semibold italic">
//                   "Work Up a Sweat. Then Treat Yourself."
//                 </p>
//                 <p className="text-gray-400 mt-2">
//                   Bring your teammates, friends, and family to hang out, catch
//                   the games, celebrate wins, and make every visit to Strikes &
//                   Shots a complete experience—on and off the field.
//                 </p>
//               </div>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* Stats Section */}
//       <section className="py-12 px-4 md:px-8 lg:px-12 bg-white relative">
//         <div className="max-w-7xl mx-auto relative z-10">
//           <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
//             {[
//               {
//                 icon: FaUsers,
//                 value: "10,000+",
//                 label: "Happy Athletes",
//                 color: "from-blue-500 to-blue-600",
//                 delay: "0s",
//               },
//               {
//                 icon: FaTrophy,
//                 value: "200+",
//                 label: "Tournaments Hosted",
//                 color: "from-yellow-500 to-orange-500",
//                 delay: "0.2s",
//               },
//               {
//                 icon: GiSoccerField,
//                 value: "8",
//                 label: "Playing Fields",
//                 color: "from-green-500 to-emerald-500",
//                 delay: "0.4s",
//               },
//               {
//                 icon: FaDumbbell,
//                 value: "24/7",
//                 label: "Gym Access",
//                 color: "from-purple-500 to-pink-500",
//                 delay: "0.6s",
//               },
//             ].map((stat, idx) => (
//               <div
//                 key={idx}
//                 className="group relative animate-scaleIn"
//                 style={{ animationDelay: stat.delay }}
//               >
//                 <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 to-red-500/0 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
//                 <div className="relative bg-white shadow-lg rounded-2xl p-6 border border-gray-200 hover:border-red-300 transition-all duration-300 hover:transform hover:-translate-y-2">
//                   <div
//                     className={`w-14 h-14 rounded-xl bg-gradient-to-r ${stat.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 group-hover:rotate-3`}
//                   >
//                     <stat.icon className="text-2xl text-white" />
//                   </div>
//                   <h3 className="text-3xl font-bold text-gray-900 mb-1">
//                     {stat.value}
//                   </h3>
//                   <p className="text-gray-600">{stat.label}</p>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>
//     </div>
//   );
// };

// export default About;
